import {
  RmaClassCodes,
  RmaCommodityTypeCodes,
  RmaInsurancePlanCodes,
  RmaLrpCommodityCode,
  RmaLrpCommodityCodes,
  RmaLrpTypeCode,
  RmaLrpTypeCodes,
  RmaSubclassCodes,
} from '@harvestiq/constants';
import { expectTypeOf } from 'expect-type';
import {
  ColumnType,
  GeneratedAlways,
  Insertable,
  Selectable,
  Updateable,
} from 'kysely';
import { z } from '@harvestiq/zod';

/**
 * Types taken from RMA ADM Layout
 * See: https://pubfs-rma.fpac.usda.gov/pub/References/actuarial_data_master/2025/ADMLayout.zip
 */
export interface DbLrpActualEndingValueDailyTable {
  id: GeneratedAlways<string>;
  createdAt: ColumnType<Date, never, never>;
  updatedAt: ColumnType<Date, never, Date>;
  recordTypeCode: ColumnType<string, string, string>;
  recordCategoryCode: ColumnType<string, string, string>;
  reinsuranceYear: ColumnType<number, number, number>;
  commodityYear: ColumnType<number, number, number>;
  commodityCode: ColumnType<
    RmaLrpCommodityCode,
    RmaLrpCommodityCode,
    RmaLrpCommodityCode
  >;
  insurancePlanCode: ColumnType<
    RmaInsurancePlanCodes,
    RmaInsurancePlanCodes,
    RmaInsurancePlanCodes
  >;
  stateCode: ColumnType<string, string, string>;
  countyCode: ColumnType<string, string, string>;
  typeCode: ColumnType<RmaLrpTypeCode, RmaLrpTypeCode, RmaLrpTypeCode>;
  practiceCode: ColumnType<string, string, string>;
  endDate: ColumnType<Date, Date, Date>;
  commodityTypeCode: ColumnType<
    RmaCommodityTypeCodes,
    RmaCommodityTypeCodes,
    RmaCommodityTypeCodes
  >;
  classCode: ColumnType<RmaClassCodes, RmaClassCodes, RmaClassCodes>;
  subClassCode: ColumnType<
    RmaSubclassCodes,
    RmaSubclassCodes,
    RmaSubclassCodes
  >;
  intendedUseCode: ColumnType<string, string, string>;
  irrigationPracticeCode: ColumnType<string, string, string>;
  croppingPracticeCode: ColumnType<string, string, string>;
  organicPracticeCode: ColumnType<string, string, string>;
  intervalCode: ColumnType<string, string, string>;
  actualEndingValueAmount: ColumnType<number, number, number>;
  lastReleasedDate: ColumnType<
    Date,
    Date | null | undefined,
    Date | null | undefined
  >;
  releasedDate: ColumnType<Date, Date, Date>;
  deletedDate: ColumnType<
    Date,
    Date | null | undefined,
    Date | null | undefined
  >;
  filingDate: ColumnType<Date, Date, Date>;
  salesEffectiveDate: ColumnType<
    Date,
    Date | null | undefined,
    Date | null | undefined
  >;
}

export type DbLrpActualEndingValueDaily =
  Selectable<DbLrpActualEndingValueDailyTable>;
export type DbLrpActualEndingValueDailyInsert =
  Insertable<DbLrpActualEndingValueDailyTable>;
export type DbLrpActualEndingValueDailyUpdate =
  Updateable<DbLrpActualEndingValueDailyTable>;

export const dbLrpActualEndingValueDailySchema = z.object({
  id: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  recordTypeCode: z.string(),
  recordCategoryCode: z.string(),
  reinsuranceYear: z.number(),
  commodityYear: z.number(),
  commodityCode: z.nativeEnum(RmaLrpCommodityCodes),
  insurancePlanCode: z.nativeEnum(RmaInsurancePlanCodes),
  stateCode: z.string(),
  countyCode: z.string(),
  typeCode: z.nativeEnum(RmaLrpTypeCodes),
  practiceCode: z.string(),
  endDate: z.date(),
  commodityTypeCode: z.nativeEnum(RmaCommodityTypeCodes),
  classCode: z.nativeEnum(RmaClassCodes),
  subClassCode: z.nativeEnum(RmaSubclassCodes),
  intendedUseCode: z.string(),
  irrigationPracticeCode: z.string(),
  croppingPracticeCode: z.string(),
  organicPracticeCode: z.string(),
  intervalCode: z.string(),
  actualEndingValueAmount: z.number(),
  lastReleasedDate: z.date().nullable(),
  releasedDate: z.date(),
  deletedDate: z.date().nullable(),
  filingDate: z.date(),
  salesEffectiveDate: z.date().nullable(),
});
expectTypeOf<DbLrpActualEndingValueDaily>().toMatchTypeOf<
  z.output<typeof dbLrpActualEndingValueDailySchema>
>();

export const dbLrpActualEndingValueDailyInsertSchema =
  dbLrpActualEndingValueDailySchema
    .omit({
      id: true,
      createdAt: true,
      updatedAt: true,
    })
    .partial({
      lastReleasedDate: true,
      deletedDate: true,
      salesEffectiveDate: true,
    });
type CreateLrpActualEndingValueDaily = z.infer<
  typeof dbLrpActualEndingValueDailyInsertSchema
>;
expectTypeOf<keyof CreateLrpActualEndingValueDaily>().toEqualTypeOf<
  keyof DbLrpActualEndingValueDailyInsert
>();
expectTypeOf<DbLrpActualEndingValueDailyInsert>().toMatchTypeOf<CreateLrpActualEndingValueDaily>();

export const lrpActualEndingValueDailyUpdateSchema =
  dbLrpActualEndingValueDailySchema
    .omit({
      id: true,
      createdAt: true,
    })
    .partial();
type UpdateLrpActualEndingValueDaily = z.infer<
  typeof lrpActualEndingValueDailyUpdateSchema
>;
expectTypeOf<keyof UpdateLrpActualEndingValueDaily>().toEqualTypeOf<
  keyof DbLrpActualEndingValueDailyUpdate
>();
expectTypeOf<DbLrpActualEndingValueDailyUpdate>().toMatchTypeOf<UpdateLrpActualEndingValueDaily>();
